import { formatNameFirstLast } from '@strata/helper-functions';
import { IconDropdownMenu } from '@strata/react-dropdown-menu';
import { DetailLabelValuePair } from '@strata/react-label-value-pairs';
import { SuccessToast } from '@strata/react-toast-message';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useNavigate } from 'react-router';

import { NotificationSettingsDialog } from 'components/treatments/dialogs/NotificationSettingsDialog';
import { handleError } from 'services/Error';
import { AppRoutes } from 'services/routes';
import { useUnwatchMutation } from 'store/api/notificationApi';

const getWatchTitle = (resource, cohortId) => {
    if (cohortId) {
        return 'TREATMENT COHORT';
    }
    if (!resource) {
        return 'RESOURCE';
    }
    return resource.toUpperCase();
};

export const WatchingWatchRow = ({ watch }) => {
    const navigate = useNavigate();
    const [unwatch, { isLoading }] = useUnwatchMutation();
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const title = getWatchTitle(watch.resource, watch.cohortId);
    const secondaryLabel = watch.cohortId ? 'Therapy Group' : 'Product';
    const patientName = formatNameFirstLast(watch.patientFirstName, watch.patientLastName);
    const canNavigate = Boolean(watch.payerId && watch.resourceId);

    const handleStopWatching = async () => {
        try {
            await unwatch(watch.watchId).unwrap();
            SuccessToast({ message: 'Stopped watching this treatment.' });
        } catch (err) {
            handleError('Unable to stop watching. Please try again.', err);
        }
    };

    const handleNavigateToTreatment = () => {
        if (!canNavigate) {
            return;
        }
        navigate(AppRoutes.treatmentDetails(watch.payerId, watch.resourceId));
    };

    return (
        <>
            <div className="sds_notification sds_watching-watch-row">
                <div className="sds_notification--seen" />
                <div
                    className={classNames(
                        'sds_notification__container',
                        'sds_notification__type--info',
                        { 'sds_watching-watch-row__container--clickable': canNavigate },
                    )}
                    {...(canNavigate
                        ? {
                              onClick: handleNavigateToTreatment,
                              onKeyDown: (e) => {
                                  if (e.key === 'Enter') {
                                      handleNavigateToTreatment();
                                  }
                              },
                              role: 'button',
                              tabIndex: 0,
                          }
                        : {})}
                >
                    <div className="sds_notification__header-row">
                        <span
                            className={classNames(
                                'sds_notification__header-row__title',
                                'sds_notification__header-row__title--info',
                            )}
                        >
                            {title}
                        </span>
                        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                        <div
                            className="sds_watching-watch-row__menu"
                            onClick={(e) => e?.stopPropagation()}
                        >
                            <IconDropdownMenu
                                title="Watch actions"
                                menuItems={[
                                    {
                                        text: 'Notification settings',
                                        onItemClick: () => setIsSettingsOpen(true),
                                    },
                                    {
                                        text: 'Stop watching',
                                        onItemClick: handleStopWatching,
                                        disabled: isLoading,
                                    },
                                ]}
                            />
                        </div>
                    </div>
                    <div className="sds_notification__additional-data flex__gap--large">
                        <DetailLabelValuePair label="Patient" value={patientName} />
                        <DetailLabelValuePair
                            label={secondaryLabel}
                            value={watch.productServiceOid}
                        />
                        <DetailLabelValuePair label="Treatment ID" value={watch.resourceId} />
                    </div>
                </div>
            </div>
            {isSettingsOpen && (
                <NotificationSettingsDialog
                    isOpen
                    onClose={() => setIsSettingsOpen(false)}
                    treatmentDetails={{
                        treatmentId: watch.resourceId,
                        treatmentCohortId: watch.cohortId,
                        payerId: watch.payerId,
                        firstName: watch.patientFirstName,
                        lastName: watch.patientLastName,
                        productServiceOid: watch.productServiceOid,
                    }}
                    existingWatch={{
                        watchId: watch.watchId,
                        kafkaTopics: watch.kafkaTopics,
                    }}
                />
            )}
        </>
    );
};

WatchingWatchRow.propTypes = {
    watch: PropTypes.shape({
        watchId: PropTypes.string.isRequired,
        resource: PropTypes.string.isRequired,
        resourceId: PropTypes.string.isRequired,
        payerId: PropTypes.string,
        cohortId: PropTypes.string,
        patientFirstName: PropTypes.string,
        patientLastName: PropTypes.string,
        productServiceOid: PropTypes.string,
        kafkaTopics: PropTypes.arrayOf(PropTypes.string),
    }).isRequired,
};
